import { useQuery } from '@tanstack/react-query';
import { getAllSearches } from 'API/linkedin-funnel-search';
import { AxiosError } from 'axios';
import { ContactsSearchStatuses, Search } from 'features/co-pilot/co-pilot.types';
import { useUserData } from 'hooks/use-user-data';
import { useEffect } from 'react';
import useErrorStore, { CustomErrorClass } from 'stores/error-store';

interface SearchResponse {
  searches: Search[];
}

const REFRESH_INTERVAL = 10 * 1000;
const STALE_TIME = 2 * 60 * 1000;

const useSearches = (funnelId: string | undefined = '', allowed: boolean) => {
  const { token } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const { data, isLoading, isError, error } = useQuery<SearchResponse>({
    queryKey: ['searches', token, funnelId],
    queryFn: ({ signal }) => getAllSearches(funnelId, true, signal),
    enabled: !!token && !!funnelId && allowed,
    staleTime: STALE_TIME,
    refetchInterval: (query) =>
      query.state.data?.searches?.some(
        (search) => search.status === ContactsSearchStatuses.RUNNING || search.status === ContactsSearchStatuses.SYNCHRONIZING_LINKEDIN_ACCOUNT
      )
        ? REFRESH_INTERVAL
        : false,
  });

  useEffect(() => {
    if (isError) {
      const customError = new CustomErrorClass(error as AxiosError);
      handleOpenErrorDialog(customError, customError.request, customError.requestType);
    }
  }, [isError, error]);

  return {
    searches: data?.searches || [],
    isLoading,
    isProcessing:
      data?.searches?.some(
        (search) => search.status === ContactsSearchStatuses.RUNNING || search.status === ContactsSearchStatuses.SYNCHRONIZING_LINKEDIN_ACCOUNT
      ) || false,
    isError,
    error,
  };
};

export default useSearches;
